import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Link,
} from '@mui/material';
import { DataGridPremium, GridToolbar, isGroupingColumn, useGridApiRef, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { apiClient } from '@/lib/apiClient';
import { GridStatePersistence } from '@/components/grids/GridStatePersistence';
import { useHasPermission } from '@/components/PermissionGate';
import { entityDetailPath } from '@/lib/pageEntityContext';
import { CountedTabs } from '@/finance/CountedTabs';
import { useCountedTabs, type CountedTab } from '@/finance/useCountedTabs';

/**
 * PlanVersionEntryAccessDialog — the "who can enter what?" surface for one plan version.
 *
 * Fetches GET /api/finance/planversion/entry-access-analysis/{id}: for every PlanHeader in the
 * version, the (node, function) each principal slot points at and the users those slots resolve to
 * right now — i.e. who will see and be able to enter each header in Plan Entry at login. Setup gaps
 * (unassigned slot, a slot nobody holds, a header nobody can enter) are flagged inline, so a
 * controller can fix the org tree before the entry round starts.
 */

interface AccessUser {
  userId: string;
  userName?: string | null;
}

interface PrincipalSlot {
  type: string;
  principalId: string;
  qualifier?: string | null;
  users: AccessUser[];
}

interface HeaderAccess {
  planHeaderId: string;
  name: string;
  ownerOrgUnitId: string;
  ownerOrgUnitName?: string | null;
  planViewId: string;
  planViewName?: string | null;
  status: string;
  assignedTo?: PrincipalSlot | null;
  owner?: PrincipalSlot | null;
  warnings: string[];
}

interface EntryAccessAnalysis {
  planVersionId: string;
  headers: HeaderAccess[];
}

/**
 * Display names for the org nodes the principal slots point at. Most slots point at a node that is
 * itself some header's Planning Org Unit (the generated convention), so those names ride along in
 * the response; only the remainder (e.g. an owner role on an ancestor without its own header) is
 * fetched — one GET per distinct unknown node, id fallback on failure.
 */
function useOrgUnitNames(analysis: EntryAccessAnalysis | undefined): Record<string, string> {
  const fromHeaders = useMemo(() => {
    const map: Record<string, string> = {};
    for (const h of analysis?.headers ?? []) {
      if (h.ownerOrgUnitName) map[h.ownerOrgUnitId] = h.ownerOrgUnitName;
    }
    return map;
  }, [analysis]);

  const [fetched, setFetched] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!analysis) return;
    const missing = new Set<string>();
    for (const h of analysis.headers) {
      for (const slot of [h.assignedTo, h.owner]) {
        if (slot && !fromHeaders[slot.principalId]) missing.add(slot.principalId);
      }
    }
    if (missing.size === 0) return;
    let cancelled = false;
    Promise.all(
      [...missing].map(async (id) => {
        try {
          const res = await apiClient.get<{ name?: string }>(`/api/core/orgunit/${id}`);
          return [id, res.data?.name || id] as const;
        } catch {
          return [id, id] as const; // keep the id visible rather than hiding the slot
        }
      }),
    ).then((entries) => {
      if (!cancelled) setFetched(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [analysis, fromHeaders]);

  return useMemo(() => ({ ...fetched, ...fromHeaders }), [fetched, fromHeaders]);
}

function UserChips({ users }: { users: AccessUser[] }) {
  const { t } = useTranslation();
  if (users.length === 0) {
    // A slot gap, like its coverage chip: red is kept for the header nobody can enter.
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={t('Finance.EntryAccess.NoHolders', { defaultValue: 'No current holders' })}
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {users.map((u) => (
        <Tooltip key={u.userId} title={u.userId}>
          <Chip size="small" variant="outlined" label={u.userName || `${u.userId.slice(0, 8)}…`} />
        </Tooltip>
      ))}
    </Box>
  );
}

function SlotCell({ slot, nodeName }: { slot?: PrincipalSlot | null; nodeName?: string }) {
  const { t } = useTranslation();
  if (!slot) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={t('Finance.EntryAccess.Unassigned', { defaultValue: 'Unassigned' })}
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="body2">
        {slot.qualifier ? `${slot.qualifier} @ ` : ''}
        {nodeName || slot.principalId}
      </Typography>
      <UserChips users={slot.users} />
    </Box>
  );
}

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation();
  const color = status === 'SUBMITTED' ? 'info' : status === 'RETURNED' ? 'warning' : undefined;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={t(`Enum:SubmissionStatus:${status}`, { defaultValue: status })}
    />
  );
}

const NO_ENTRY_ACCESS = 'NoEntryAccess';

/** Each coverage tab is a filter over the same headers, from the warnings the server put on each. */
type CoverageTab = 'all' | 'gaps' | 'noEntryAccess';
const COVERAGE_TABS: CountedTab<CoverageTab, HeaderAccess>[] = [
  { value: 'all', includes: () => true },
  { value: 'gaps', includes: h => h.warnings.length > 0, hideWhenEmpty: true },
  { value: 'noEntryAccess', includes: h => h.warnings.includes(NO_ENTRY_ACCESS), hideWhenEmpty: true },
];

function coverageTabLabel(tab: CoverageTab, n: number, t: TFunction): string {
  switch (tab) {
    case 'all': return t('Finance.EntryAccess.TabAll', { defaultValue: 'All ({{n}})', n });
    case 'gaps': return t('Finance.EntryAccess.TabGaps', { defaultValue: 'Setup gaps ({{n}})', n });
    case 'noEntryAccess': return t('Finance.EntryAccess.TabNoEntryAccess', { defaultValue: 'Nobody can enter ({{n}})', n });
  }
}

const WARNING_FALLBACKS: Record<string, string> = {
  NoEntryAccess: 'No entry access',
  AssignedToUnassigned: 'Assignee unassigned',
  AssignedToNoHolders: 'Assignee has no holders',
  OwnerUnassigned: 'Owner unassigned',
  OwnerNoHolders: 'Owner has no holders',
};

/** The header's gaps, the bottom line (nobody can enter) first; empty when it is fully covered. */
function coverageLabels(h: HeaderAccess, t: TFunction): string[] {
  const ordered = [...h.warnings].sort((a, b) => Number(b === NO_ENTRY_ACCESS) - Number(a === NO_ENTRY_ACCESS));
  return ordered.map(w => t(`Finance.EntryAccess.Warning.${w}`, { defaultValue: WARNING_FALLBACKS[w] ?? w }));
}

function CoverageCell({ header }: { header: HeaderAccess }) {
  const { t } = useTranslation();
  const labels = coverageLabels(header, t);
  if (labels.length === 0) {
    return <Chip size="small" color="success" variant="outlined" label={t('Finance.EntryAccess.Covered', { defaultValue: 'Covered' })} />;
  }
  const bottomLine = header.warnings.includes(NO_ENTRY_ACCESS);
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {labels.map((label, i) => (
        <Chip key={label} size="small" color={bottomLine && i === 0 ? 'error' : 'warning'} variant={bottomLine && i === 0 ? 'filled' : 'outlined'} label={label} />
      ))}
    </Box>
  );
}

/** What a slot cell shows, as text — the grid filters, sorts and quick-searches on it (holders included). */
function slotText(slot: PrincipalSlot | null | undefined, nodeName: string | undefined, t: TFunction): string {
  if (!slot) return t('Finance.EntryAccess.Unassigned', { defaultValue: 'Unassigned' });
  const target = `${slot.qualifier ? `${slot.qualifier} @ ` : ''}${nodeName || slot.principalId}`;
  const holders = slot.users.length > 0
    ? slot.users.map(u => u.userName || u.userId).join(', ')
    : t('Finance.EntryAccess.NoHolders', { defaultValue: 'No current holders' });
  return `${target} · ${holders}`;
}

/**
 * A click that opens the link here: the primary button, no modifier. Cmd/Ctrl/Shift/Alt-click opens
 * the header in another tab or window, so the dialog stays open behind it.
 */
const isPlainClick = (e: MouseEvent<HTMLElement>) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

/**
 * Group rows carry no header (the value getters check planHeaderId for the same reason). MUI draws
 * a group's label with the grouped column's own renderCell, so in the grouping column a group row
 * shows its value; in every other column its cell stays empty.
 */
const leafOnly = (render: (h: HeaderAccess) => ReactNode) =>
  (params: GridRenderCellParams<HeaderAccess>) => {
    if (params.rowNode.type === 'leaf') return render(params.row);
    return isGroupingColumn(params.field) ? (params.formattedValue ?? params.value ?? null) : null;
  };

function useAccessColumns(nodeNames: Record<string, string>, onNavigate: () => void): GridColDef<HeaderAccess>[] {
  const { t } = useTranslation();
  const canOpenHeader = useHasPermission('Finance.PlanHeader.Read');
  return useMemo(() => [
    {
      field: 'name',
      headerName: t('Finance.EntryAccess.ColHeader', { defaultValue: 'Header' }),
      width: 240,
      // To the header itself, where its Assigned to and Owner are set: a gap found here is fixed
      // there. Plain text without the read permission, as a reference cell does.
      renderCell: leafOnly(h => (canOpenHeader ? (
        <Link component={RouterLink} to={entityDetailPath('Finance', 'PlanHeader', h.planHeaderId)} variant="body2" underline="hover" onClick={e => { if (isPlainClick(e)) onNavigate(); }}>
          {h.name}
        </Link>
      ) : h.name)),
    },
    {
      field: 'ownerOrgUnitName',
      headerName: t('Finance.EntryAccess.ColOrgUnit', { defaultValue: 'Planning org unit' }),
      width: 180,
      valueGetter: (_v, h) => h.ownerOrgUnitName || h.ownerOrgUnitId,
    },
    {
      field: 'planViewName',
      headerName: t('DisplayName:PlanHeader.planViewId', { defaultValue: 'Plan View' }),
      width: 180,
      valueGetter: (_v, h) => h.planViewName || h.planViewId,
    },
    {
      field: 'status',
      headerName: t('Finance.EntryAccess.ColStatus', { defaultValue: 'Status' }),
      width: 130,
      valueGetter: (_v, h) => (h.planHeaderId ? t(`Enum:SubmissionStatus:${h.status}`, { defaultValue: h.status }) : undefined),
      renderCell: leafOnly(h => <StatusChip status={h.status} />),
    },
    {
      field: 'coverage',
      headerName: t('Finance.EntryAccess.ColCoverage', { defaultValue: 'Entry access' }),
      width: 220,
      valueGetter: (_v, h) => (h.planHeaderId
        ? coverageLabels(h, t).join(', ') || t('Finance.EntryAccess.Covered', { defaultValue: 'Covered' })
        : undefined),
      renderCell: leafOnly(h => <CoverageCell header={h} />),
    },
    {
      field: 'assignedTo',
      headerName: t('Finance.EntryAccess.ColAssignedTo', { defaultValue: 'Assigned to (contributor)' }),
      width: 260,
      valueGetter: (_v, h) => (h.planHeaderId ? slotText(h.assignedTo, h.assignedTo ? nodeNames[h.assignedTo.principalId] : undefined, t) : undefined),
      renderCell: leafOnly(h => <SlotCell slot={h.assignedTo} nodeName={h.assignedTo ? nodeNames[h.assignedTo.principalId] : undefined} />),
    },
    {
      // A header is never approved — only the version is. The Owner's power here is to send a
      // submitted header back for rework.
      field: 'owner',
      headerName: t('Finance.EntryAccess.ColOwner', { defaultValue: 'Owner (may return)' }),
      width: 260,
      valueGetter: (_v, h) => (h.planHeaderId ? slotText(h.owner, h.owner ? nodeNames[h.owner.principalId] : undefined, t) : undefined),
      renderCell: leafOnly(h => <SlotCell slot={h.owner} nodeName={h.owner ? nodeNames[h.owner.principalId] : undefined} />),
    },
  ], [t, nodeNames, onNavigate, canOpenHeader]);
}

export interface PlanVersionEntryAccessDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

const EMPTY_HEADERS: HeaderAccess[] = [];

export function PlanVersionEntryAccessDialog({ entityId, open, onClose }: PlanVersionEntryAccessDialogProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['plan-version-entry-access', entityId],
    queryFn: async () =>
      (await apiClient.get<EntryAccessAnalysis>(`/api/finance/planversion/entry-access-analysis/${entityId}`)).data,
    enabled: open,
    // A gap is fixed on the header in another tab; coming back shows the assignments as they are now.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const nodeNames = useOrgUnitNames(data);
  const columns = useAccessColumns(nodeNames, onClose);

  const distinctUserCount = useMemo(() => {
    const ids = new Set<string>();
    for (const h of data?.headers ?? []) {
      for (const slot of [h.assignedTo, h.owner]) {
        for (const u of slot?.users ?? []) ids.add(u.userId);
      }
    }
    return ids.size;
  }, [data]);

  const apiRef = useGridApiRef();
  const [tab, setTab] = useState<CoverageTab>('all');
  const { counts, visibleTabs, activeTab, tabRows } = useCountedTabs(data?.headers ?? EMPTY_HEADERS, COVERAGE_TABS, tab, 'all');

  // A fixed-height dialog the grid fills and scrolls inside, so it renders only the rows in view.
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth slotProps={{ paper: { sx: { height: '85vh' } } }}>
      <DialogTitle>{t('Finance.EntryAccess.Title', { defaultValue: 'Explain entry access' })}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('Finance.EntryAccess.Loading', { defaultValue: 'Resolving assignments…' })}
            </Typography>
          </Box>
        )}

        {!!error && (
          <Alert severity="error">
            {t('Finance.EntryAccess.LoadFailed', { defaultValue: 'Failed to load the entry access analysis.' })}
          </Alert>
        )}

        {data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <CountedTabs
                tabs={visibleTabs}
                counts={counts}
                value={activeTab}
                onChange={setTab}
                label={(value, n) => coverageTabLabel(value, n, t)}
              />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={t('Finance.EntryAccess.UsersWithAccess', {
                  defaultValue: '{{n}} users with entry access',
                  n: distinctUserCount,
                })}
              />
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <DataGridPremium
                apiRef={apiRef}
                rows={tabRows}
                columns={columns}
                getRowId={h => h.planHeaderId}
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                disablePivoting
                disableAggregation
                initialState={{ density: 'compact' }}
                localeText={{ noRowsLabel: t('Finance.EntryAccess.Empty', { defaultValue: 'This version has no plan headers yet.' }) }}
                slots={{ toolbar: GridToolbar }}
                slotProps={{ toolbar: { showQuickFilter: true } }}
                sx={{ flex: 1, minHeight: 0, '& .MuiDataGrid-cell': { py: 0.75, alignItems: 'flex-start' } }}
              />
              {/* AFTER the grid on purpose — the grid mounts once the analysis is in. */}
              <GridStatePersistence apiRef={apiRef} module="Finance" entity="PlanVersionEntryAccess" variant="dialog" />
            </Box>

            <Alert severity="info">
              {t('Finance.EntryAccess.Footer', {
                defaultValue:
                  'Listed users see and may enter their headers; owners may additionally return submitted ones. ' +
                  'Users holding the ViewSubtree / ViewAll permissions see headers read-only and are not listed here. ' +
                  'A SUBMITTED header is read-only for everyone until returned. ' +
                  'Membership changes take effect within about two minutes.',
              })}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
      </DialogActions>
    </Dialog>
  );
}
